import { Button, Grid, Typography, Box, Hidden, FormControl, InputLabel, Select, Autocomplete, TextField } from "@mui/material";
import { Search as IconSearch, ArrowBack as IconVolver } from "@mui/icons-material";
import { Link } from "react-router-dom";
import api from "../../services/api";
import * as sesionService from "../../services/sesionService";
import * as periodoService from "../../services/periodoService";
import * as personaService from "../../services/personaService";
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useRef, useState } from "react";
import { setValue, setListTerapeutas, setListPeriodos } from '../../redux/planillaSearchSlice';
import DataGridBase from "../../components/common/DataGridBase"
import AutocompleteLista from '../../components/common/AutocompleteLista';
import GraficoBarras from "../../components/common/GraficoBarras"
import FiltroFilial from "../../components/common/FiltroFilial";
import { useFiltroFilial } from "../../hooks/useFiltroFilial";

// Resumen de tiempos de los trabajadores: horas de sesion validas por
// terapeuta y periodo, con una grafica de barras por terapeuta. Se puede
// filtrar por terapeuta (muchos periodos) o por periodo (muchos terapeutas).
const Index = () => {
    const filters = useSelector((state) => state.planillaSearch.filters);
    const listTerapeutas = useSelector((state) => state.planillaSearch.listTerapeutas);
    const listPeriodos = useSelector((state) => state.planillaSearch.listPeriodos);

    const [cargando, setCargando] = useState(true);
    const [results, setResultsLocal] = useState([]);
    const dispatch = useDispatch();
    // La filial del usuario: si tiene una, el filtro queda en esa y bloqueado.
    const { filiales, idFilial, setIdFilial, bloqueado, terapeutas: terapeutasFilial } = useFiltroFilial();
    const perfilCargado = useSelector((state) => state.seguridad?.perfilCargado);
    const idFilialUsuario = useSelector((state) => state.seguridad?.idFilial) || 0;
    const primeraCarga = useRef(false);

    const fetchResults = async (filial = idFilial, terapeuta = filters.idTerapeuta, periodo = filters.idPeriodo) => {
        setCargando(true);

        await sesionService.getsSesionPlanillaView({
                idTerapeuta: terapeuta,
                idPeriodo: periodo,
                idFilial: filial || 0,
            })
            .then((data) => {
                setResultsLocal(data);
                setCargando(false);
            })
            .catch((err) => {
                setCargando(false);
                console.log("Err: ", err)
            });
    }
    const fetchTerapeutas = async () => {
        const data = await personaService.getsListNaturalPersonByTypeAndName(25, "")
            .catch((err) => {
                setCargando(false);
                console.log("Err: ", err);
            });

        dispatch(setListTerapeutas(data));
    }
    const fetchPeriodos = async () => {
        await periodoService.getsPeriodoView({
                idTipoTerapia: 0,
                idEstadoApertura: 87,
                mesesHaciaAtras: 0,
                idEstado: 2,
            })
            .then((data) => {
                dispatch(setListPeriodos(data));
            })
            .catch((err) => {
                setCargando(false);
                console.log("Err: ", err)
            });
    }

    useEffect(() => {
        fetchTerapeutas();
        fetchPeriodos();
    }, []);

    // La primera lectura espera a saber la filial del usuario. Si tiene una,
    // no se listan las horas de las otras sedes.
    useEffect(() => {
        if (!perfilCargado || primeraCarga.current) return;
        if (idFilialUsuario > 0 && idFilial !== idFilialUsuario) return;

        primeraCarga.current = true;
        fetchResults(idFilialUsuario > 0 ? idFilialUsuario : idFilial);
    }, [perfilCargado, idFilial, idFilialUsuario]);

    const columns = [
        { field: 'id', headerName: 'ID', width: 50, minWidth: 45, sortable: true, headerAlign: 'center', align: 'center',
            renderCell: (p) => <span style={{ fontSize: '0.75rem', color: '#666', fontFamily: 'monospace' }}>{p.value != null ? p.value : p.row.id}</span> },
        { field: 'seudonimo', headerName: 'Seudónimo', width: 160, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'terapeuta', headerName: 'Terapeuta', width: 200, sortable: true, headerAlign: 'center' },
        { field: 'codigoPeriodo', headerName: 'Periodo', width: 100, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'numeroSesiones', headerName: 'Sesiones', width: 90, sortable: true, headerAlign: 'center', align: 'center' },
        { field: 'numeroSesionesValidadas', headerName: 'Validadas', width: 95, sortable: true, headerAlign: 'center', align: 'center' },
        { field: 'minutosSesion', headerName: 'Minutos', width: 90, sortable: true, headerAlign: 'center', align: 'center' },
        { field: 'horasSesion', headerName: 'Horas', width: 90, sortable: true, headerAlign: 'center', align: 'center' },
    ];

    // Un barra por terapeuta: se suman sus horas de todos los periodos del
    // filtro, porque el mismo terapeuta puede tener varios periodos.
    const datosGrafico = () => {
        const porTerapeuta = {};

        results.forEach((row) => {
            const id = row.idTerapeuta;
            if (!porTerapeuta[id]) {
                porTerapeuta[id] = {
                    etiqueta: row.seudonimo || row.terapeuta,
                    valor: 0,
                    descripcion: "",
                };
            }
            porTerapeuta[id].valor += Number(row.horasSesion) || 0;
        });

        return Object.keys(porTerapeuta).map((id) => {
            const item = porTerapeuta[id];
            const nombre = results.find((r) => r.idTerapeuta == id)?.terapeuta || "";
            const periodos = results
                .filter((r) => r.idTerapeuta == id)
                .map((r) => r.codigoPeriodo)
                .join(", ");

            return {
                ...item,
                descripcion: nombre + " - " + item.valor.toFixed(2) + " h"
                    + (periodos ? " (" + periodos + ")" : ""),
            };
        }).sort((a, b) => b.valor - a.valor);
    }

    // La carga del autocomplete de terapeutas: con texto busca en el servidor.
    const cargarTerapeutas = useCallback(async (texto) => {
        const response = await api.get(
            `/Persona/GetsListNaturalPersonByTypeAndName?idType=25&name=${encodeURIComponent(texto || "")}`)
            .catch((err) => {
                console.log("Err: ", err);
                return { data: { data: [] } };
            });

        const lista = (response?.data?.data || []).map((row) => ({ id: row.id, descripcion: row.descripcion }));

        if (!(idFilial > 0)) return lista;

        const ids = new Set(terapeutasFilial.map((t) => t.id));

        return lista.filter((row) => ids.has(row.id));
    }, [idFilial, terapeutasFilial])

    return (
        <div>
            <Grid container>
                <Grid item>
                    {/* Esta pantalla se abre tambien desde la portada de
                        reportes: se puede volver a ella. */}
                    <Button component={Link} to="/reporte" startIcon={<IconVolver />} size="small"
                        sx={{ paddingLeft: 0, marginBottom: 1 }}>
                        Volver a Reportes
                    </Button>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Resumen de Tiempos
                    </Typography>
                </Grid>
                <Grid container spacing={1} padding={"12px 0px 0px 0px"}>
                    <Grid item xs={12} sm={12}>
                        <Grid container item xs={12} sm={12} spacing={1} padding={0}>
                            <Grid item xs={12} sm={3}>
                                <FiltroFilial
                                    filiales={filiales}
                                    idFilial={idFilial}
                                    bloqueado={bloqueado}
                                    onChange={(id) => {
                                        setIdFilial(id);
                                        dispatch(setValue({ name: "idTerapeuta", value: 0 }));
                                        dispatch(setValue({ name: "terapeuta", value: "" }));
                                        fetchResults(id, 0, filters.idPeriodo);
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <FormControl fullWidth>
                                      {/* La lista de terapeutas crece con cada terapeuta que se
                                          registra, asi que el desplegable es un autocomplete: al
                                          abrirlo estan los primeros 50 (en orden alfabetico) y al
                                          escribir se busca en el servidor. */}
                                      <AutocompleteLista
                                          label="Terapeuta"
                                          name="idTerapeuta"
                                          value={filters.idTerapeuta || 0}
                                          texto={filters.terapeuta || ""}
                                          cargar={cargarTerapeutas}
                                          onChange={(id, texto) => {
                                              dispatch(setValue({name: "idTerapeuta", value: id}))
                                              dispatch(setValue({name: "terapeuta", value: texto}))
                                          }}
                                          size="small"
                                      />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="input-periodo" shrink>Periodo</InputLabel>
                                    <Select
                                        native
                                        label="Periodo"
                                        value={filters.idPeriodo}
                                        onChange={(event) => {
                                            dispatch(setValue({name: "idPeriodo", value: event.target.value}))
                                        }}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter") {
                                                event.preventDefault();
                                                fetchResults();
                                            }
                                        }}
                                        inputProps={{
                                            name: 'idPeriodo',
                                            id: 'input-periodo',
                                        }}
                                        size="small"
                                    >
                                        <option key={0} value={0}>{"(Todos)"}</option>
                                        {listPeriodos.map((row) => (
                                            <option key={row.id} value={row.id}>{row.codigo}</option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={4} sm={1} display={"flex"} alignItems={"center"}>
                                <Button variant="outlined" size="medium" fullWidth onClick={() => {
                                    fetchResults(idFilial)
                                }}>
                                    {<Hidden smDown></Hidden>}
                                    <IconSearch />
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <GraficoBarras
                            datos={datosGrafico()}
                            titulo={"Horas por terapeuta"}
                            subtitulo={"Solo cuentan las sesiones validadas. El globo de cada barra "
                                + "detalla el nombre y los periodos del terapeuta."}
                            formatoValor={(v) => v.toFixed(2) + " h"}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                            <DataGridBase
                                rows={results}
                                columns={columns}
                                loading={cargando}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Grid>
        </div>
    )
}
export default Index